import React, { useState } from 'react';
import { X, Clock, AlertTriangle, User, Truck } from 'lucide-react';

function timeToSeconds(timeStr) {
  if (!timeStr) return 0;
  if (typeof timeStr === 'string' && timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1]?.slice(0, 8) || '00:00:00';
    return timeToSeconds(timePart);
  }
  if (typeof timeStr === 'string' && timeStr.includes(':')) {
    const parts = timeStr.split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    const s = parseInt(parts[2], 10) || 0;
    return h * 3600 + m * 60 + s;
  }
  return 0;
}

function secondsToTimeString(sec) {
  const h = Math.floor(sec / 3600) % 24;
  const m = Math.floor((sec % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default function DriverTimelinePanel({
  isOpen,
  onClose,
  drivers = [],
  driverShifts = [],
  jobs = [],
  stops = [],
  trucks = [],
  clients = [],
  currentDate,
  onSelectStop,
  onSelectJob
}) {
  const [hoveredStop, setHoveredStop] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [timelineGrouping, setTimelineGrouping] = useState('driver'); // 'driver' | 'truck'

  if (!isOpen) return null;

  const curDateStr = (currentDate instanceof Date
    ? currentDate.toISOString().slice(0, 10)
    : String(currentDate || '').slice(0, 10)) || '2026-08-17';

  // 1. Map stops to jobs
  const stopsByJobId = {};
  stops.forEach(s => {
    if (s.job_id) {
      if (!stopsByJobId[s.job_id]) stopsByJobId[s.job_id] = [];
      stopsByJobId[s.job_id].push(s);
    }
  });

  // Sort stops chronologically
  Object.keys(stopsByJobId).forEach(jobId => {
    stopsByJobId[jobId].sort((a, b) => {
      const tA = timeToSeconds(a.estimated_arrival || '08:00');
      const tB = timeToSeconds(b.estimated_arrival || '08:00');
      return tA - tB;
    });
  });

  // 2. Helper to compute Job Start and End times in seconds
  const getJobTimeRange = (job) => {
    const jobStops = stopsByJobId[job.id] || [];
    let startSec = job.estimated_departure ? timeToSeconds(job.estimated_departure) : 7 * 3600 + 30 * 60; // default 07:30
    let endSec = startSec + 4 * 3600; // default 4 hours duration if no stops

    if (jobStops.length > 0) {
      const firstStopArrival = timeToSeconds(jobStops[0].estimated_arrival || '08:00');
      if (firstStopArrival < startSec) {
        startSec = firstStopArrival - 15 * 60; // 15m before first arrival
      }
      const lastStop = jobStops[jobStops.length - 1];
      const lastStopArrival = timeToSeconds(lastStop.estimated_arrival || '12:00');
      const lastService = (lastStop.service_time_mins || 10) * 60;
      const lastDeparture = lastStopArrival + lastService;
      endSec = Math.max(startSec + 1800, lastDeparture + 30 * 60); // 30m return buffer
    }

    return { startSec, endSec };
  };

  // 3. Filter driver shifts for current date
  const dayShifts = (driverShifts || []).filter(s => {
    const sDate = String(s.start_datetime || '').slice(0, 10);
    return sDate === curDateStr;
  });

  // Map shifts by driver_id
  const shiftsByDriverId = {};
  drivers.forEach(d => { shiftsByDriverId[d.id] = []; });
  dayShifts.forEach(s => {
    if (shiftsByDriverId[s.driver_id]) {
      shiftsByDriverId[s.driver_id].push(s);
    }
  });

  // 4. Compute global Day Window across all active jobs AND driver planning shifts
  let earliestSec = 24 * 3600;
  let latestSec = 0;
  let hasActivity = false;

  // Check jobs
  jobs.forEach(job => {
    hasActivity = true;
    const { startSec, endSec } = getJobTimeRange(job);
    if (startSec < earliestSec) earliestSec = startSec;
    if (endSec > latestSec) latestSec = endSec;
  });

  // Check driver shifts
  dayShifts.forEach(s => {
    hasActivity = true;
    const sStart = timeToSeconds(s.start_datetime);
    const sEnd = timeToSeconds(s.end_datetime);
    if (sStart < earliestSec) earliestSec = sStart;
    if (sEnd > latestSec) latestSec = sEnd;
  });

  if (!hasActivity || earliestSec >= latestSec) {
    earliestSec = 7 * 3600; // 07:00
    latestSec = 19 * 3600;  // 19:00
  }

  // 30 min buffer before earliest and 30 min buffer after latest
  const dayStartSec = Math.max(0, earliestSec - 30 * 60);
  const dayEndSec = Math.min(24 * 3600, latestSec + 30 * 60);
  const daySpanSec = Math.max(3600, dayEndSec - dayStartSec);

  // Time conversion helper
  const timeToPct = (sec) => Math.max(0, Math.min(100, ((sec - dayStartSec) / daySpanSec) * 100));

  // Generate Hour Ticks for the timeline header
  const hourTicks = [];
  const startHour = Math.floor(dayStartSec / 3600);
  const endHour = Math.ceil(dayEndSec / 3600);

  for (let h = startHour; h <= endHour; h++) {
    const sec = h * 3600;
    if (sec >= dayStartSec && sec <= dayEndSec) {
      hourTicks.push({
        label: `${String(h).padStart(2, '0')}:00`,
        pct: timeToPct(sec)
      });
    }
  }

  // 5. Group jobs by DRIVER
  const jobsByDriver = {};
  drivers.forEach(d => { jobsByDriver[d.id] = []; });
  
  const unassignedDriverJobs = [];
  jobs.forEach(j => {
    if (j.driver_id && jobsByDriver[j.driver_id]) {
      jobsByDriver[j.driver_id].push(j);
    } else {
      unassignedDriverJobs.push(j);
    }
  });

  const activeDriverList = [
    ...drivers.filter(d => (jobsByDriver[d.id] || []).length > 0 || (shiftsByDriverId[d.id] || []).length > 0),
    ...drivers.filter(d => (jobsByDriver[d.id] || []).length === 0 && (shiftsByDriverId[d.id] || []).length === 0)
  ];

  if (unassignedDriverJobs.length > 0) {
    activeDriverList.push({
      id: 'unassigned_driver',
      first_name: 'Missions',
      last_name: 'Sans chauffeur',
      isUnassigned: true
    });
    jobsByDriver['unassigned_driver'] = unassignedDriverJobs;
  }

  // 6. Group jobs by TRUCK
  const jobsByTruck = {};
  trucks.forEach(t => { jobsByTruck[t.id] = []; });

  const unassignedTruckJobs = [];
  jobs.forEach(j => {
    if (j.truck_id && jobsByTruck[j.truck_id]) {
      jobsByTruck[j.truck_id].push(j);
    } else {
      unassignedTruckJobs.push(j);
    }
  });

  const activeTruckList = [
    ...trucks.filter(t => (jobsByTruck[t.id] || []).length > 0),
    ...trucks.filter(t => (jobsByTruck[t.id] || []).length === 0)
  ];

  if (unassignedTruckJobs.length > 0) {
    activeTruckList.push({
      id: 'unassigned_truck',
      name: 'Missions sans véhicule',
      license_plate: 'Non assigné',
      isUnassigned: true
    });
    jobsByTruck['unassigned_truck'] = unassignedTruckJobs;
  }

  // Check superposition / overlapping jobs for a row
  const checkJobOverlap = (rowJobs, targetJob) => {
    const tRange = getJobTimeRange(targetJob);
    return rowJobs.some(otherJob => {
      if (otherJob.id === targetJob.id) return false;
      const oRange = getJobTimeRange(otherJob);
      return tRange.startSec < oRange.endSec && tRange.endSec > oRange.startSec;
    });
  };

  // Helper to compute curve data for any set of jobs
  const buildJobCurves = (rowJobs) => {
    const jobCurves = [];

    rowJobs.forEach(job => {
      const jStops = stopsByJobId[job.id] || [];
      const { startSec, endSec } = getJobTimeRange(job);
      const truckObj = trucks.find(t => t.id === job.truck_id);
      const maxPayloadKg = Math.max(100, parseFloat(truckObj?.max_payload_weight_kg) || 1300);

      // Initial departure load at hub
      const initialWeight = jStops
        .filter(s => s.type !== 'pickup')
        .reduce((sum, s) => sum + parseFloat(s.weight_kg || 0), 0);

      let events = [];
      jStops.forEach(s => {
        const arrSec = timeToSeconds(s.estimated_arrival || '08:00');
        const servSec = (s.service_time_mins || 10) * 60;
        const depSec = arrSec + servSec;
        const w = parseFloat(s.weight_kg || 0);
        const isPickup = s.type === 'pickup';
        events.push({ timeSec: depSec, deltaWeight: isPickup ? w : -w, stop: s });
      });
      events.sort((a, b) => a.timeSec - b.timeSec);

      let simW = initialWeight;
      let maxWeightReached = initialWeight;
      events.forEach(evt => {
        simW = Math.max(0, simW + evt.deltaWeight);
        if (simW > maxWeightReached) maxWeightReached = simW;
      });

      const peakWeight = Math.max(maxWeightReached, maxPayloadKg);

      const calcY = (w) => {
        if (peakWeight <= 0) return 100;
        const ratio = Math.max(0, w) / peakWeight;
        const yVal = 100 - (ratio * 50); // 0 -> 100, peakWeight -> 50
        return Math.max(50, Math.min(100, yVal));
      };

      const y100 = 100 - ((maxPayloadKg / peakWeight) * 50);
      const top100Px = 62 - ((maxPayloadKg / peakWeight) * 30);

      const startX = timeToPct(startSec);
      const endX = timeToPct(endSec);

      let polyPoints = [];
      let pathD = `M ${startX} 100 L ${startX} ${calcY(initialWeight)}`;

      polyPoints.push(`${startX},100`);
      polyPoints.push(`${startX},${calcY(initialWeight)}`);

      let curX = startX;
      let curW = initialWeight;
      let overloadPolys = [];
      let currentOverloadPts = [];

      if (curW > maxPayloadKg) {
        currentOverloadPts.push(`${startX},${y100}`);
        currentOverloadPts.push(`${startX},${calcY(curW)}`);
      }

      events.forEach(evt => {
        const evtX = timeToPct(evt.timeSec);
        const prevY = calcY(curW);
        const nextW = Math.max(0, curW + evt.deltaWeight);
        const nextY = calcY(nextW);

        polyPoints.push(`${evtX},${prevY}`);
        polyPoints.push(`${evtX},${nextY}`);
        pathD += ` L ${evtX} ${prevY} L ${evtX} ${nextY}`;

        if (curW > maxPayloadKg) {
          currentOverloadPts.push(`${evtX},${prevY}`);
          if (nextW <= maxPayloadKg) {
            currentOverloadPts.push(`${evtX},${y100}`);
            overloadPolys.push({
              polygon: currentOverloadPts.join(' '),
              pathD: `M ${currentOverloadPts[0].split(',')[0]} ${y100} L ${currentOverloadPts.slice(1, -1).map(p => p.replace(',', ' ')).join(' L ')} L ${evtX} ${y100}`
            });
            currentOverloadPts = [];
          }
        } else if (nextW > maxPayloadKg) {
          currentOverloadPts.push(`${evtX},${y100}`);
          currentOverloadPts.push(`${evtX},${nextY}`);
        }

        curW = nextW;
        curX = evtX;
      });

      const finalY = calcY(curW);
      polyPoints.push(`${endX},${finalY}`);
      polyPoints.push(`${endX},100`);
      pathD += ` L ${endX} ${finalY} L ${endX} 100`;

      if (curW > maxPayloadKg && currentOverloadPts.length > 0) {
        currentOverloadPts.push(`${endX},${finalY}`);
        currentOverloadPts.push(`${endX},${y100}`);
        overloadPolys.push({
          polygon: currentOverloadPts.join(' '),
          pathD: `M ${currentOverloadPts[0].split(',')[0]} ${y100} L ${currentOverloadPts.slice(1, -1).map(p => p.replace(',', ' ')).join(' L ')} L ${endX} ${y100}`
        });
      }

      jobCurves.push({
        jobId: job.id,
        top100Px,
        basePolygon: polyPoints.join(' '),
        basePathD: pathD,
        overloadPolys
      });
    });

    return jobCurves;
  };

  return (
    <div
      className="left-detail-pane"
      style={{
        width: '1070px',
        maxWidth: '95vw',
        flexShrink: 0,
        backgroundColor: '#0f172a',
        color: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #334155',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      {/* DRAWER HEADER WITH GROUPING TOGGLE */}
      <div style={{
        padding: '10px 18px',
        borderBottom: '1px solid #334155',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#1e293b',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={17} color="#38bdf8" />
            <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc' }}>
              Planning Timeline
            </h2>
          </div>

          {/* Toggle Button: Chauffeurs vs Véhicules */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: '6px',
            padding: '2px',
            gap: '2px'
          }}>
            <button
              type="button"
              onClick={() => setTimelineGrouping('driver')}
              style={{
                background: timelineGrouping === 'driver' ? '#0284c7' : 'transparent',
                color: timelineGrouping === 'driver' ? '#ffffff' : '#94a3b8',
                border: 'none',
                borderRadius: '4px',
                padding: '3px 10px',
                fontSize: '0.74rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                transition: 'all 0.15s ease'
              }}
            >
              <User size={13} />
              <span>Chauffeurs ({activeDriverList.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setTimelineGrouping('truck')}
              style={{
                background: timelineGrouping === 'truck' ? '#0284c7' : 'transparent',
                color: timelineGrouping === 'truck' ? '#ffffff' : '#94a3b8',
                border: 'none',
                borderRadius: '4px',
                padding: '3px 10px',
                fontSize: '0.74rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                transition: 'all 0.15s ease'
              }}
            >
              <Truck size={13} />
              <span>Véhicules ({activeTruckList.length})</span>
            </button>
          </div>
        </div>

        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#ef4444',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center'
          }}
          title="Fermer"
        >
          <X size={18} />
        </button>
      </div>

      {/* TIMELINE TIME AXIS HEADER */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: '#0f172a',
        borderBottom: '1px solid #334155',
        padding: '6px 0',
        userSelect: 'none',
        flexShrink: 0
      }}>
        {/* Left column spacer */}
        <div style={{
          width: '190px',
          minWidth: '190px',
          paddingLeft: '16px',
          fontSize: '0.70rem',
          fontWeight: '700',
          color: '#64748b'
        }}>
          {timelineGrouping === 'driver' ? 'CHAUFFEURS' : 'VÉHICULES'}
        </div>

        {/* Time axis ticks */}
        <div style={{ flex: 1, position: 'relative', height: '18px', marginRight: '20px' }}>
          {hourTicks.map((tick, idx) => (
            <div
              key={idx}
              style={{
                position: 'absolute',
                left: `${tick.pct}%`,
                transform: 'translateX(-50%)',
                fontSize: '0.70rem',
                fontWeight: '700',
                color: '#94a3b8'
              }}
            >
              {tick.label}
            </div>
          ))}
        </div>
      </div>

      {/* ROWS CONTAINER (SCROLLABLE) */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '0'
      }}>
        {/* ========================================================= */}
        {/* 1. CHAUFFEURS GROUPING VIEW */}
        {/* ========================================================= */}
        {timelineGrouping === 'driver' && activeDriverList.map(driver => {
          const driverJobs = jobsByDriver[driver.id] || [];
          const driverShiftsToday = shiftsByDriverId[driver.id] || [];
          const hasJobs = driverJobs.length > 0;
          const jobCurves = buildJobCurves(driverJobs);

          return (
            <div
              key={driver.id}
              style={{
                display: 'flex',
                alignItems: 'stretch',
                minHeight: '62px',
                height: '62px',
                borderBottom: '1px solid #334155',
                background: '#090d16',
                position: 'relative'
              }}
            >
              {/* DRIVER INFO LEFT COLUMN */}
              <div style={{
                width: '190px',
                minWidth: '190px',
                padding: '6px 12px',
                borderRight: '1px solid #334155',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#0f172a',
                zIndex: 10
              }}>
                <div style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  background: driver.isUnassigned ? '#ef4444' : '#0284c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.74rem',
                  fontWeight: '800',
                  color: '#fff',
                  flexShrink: 0
                }}>
                  {driver.isUnassigned ? '?' : `${driver.first_name?.[0] || ''}${driver.last_name?.[0] || ''}`}
                </div>

                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#f1f5f9', lineHeight: '1.2', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {driver.first_name} {driver.last_name}
                  </div>
                  {driver.driving_license && (
                    <div style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                      Permis {driver.driving_license}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT TIMELINE AXIS & TRACK AREA */}
              <div style={{
                flex: 1,
                position: 'relative',
                marginRight: '20px',
                display: 'flex',
                alignItems: 'stretch',
                background: '#090d16'
              }}>
                {/* DRIVER SHIFT WORKING WINDOWS */}
                {driverShiftsToday.map(shift => {
                  const sStart = timeToSeconds(shift.start_datetime);
                  const sEnd = timeToSeconds(shift.end_datetime);
                  const shiftLeftPct = timeToPct(sStart);
                  const shiftRightPct = timeToPct(sEnd);
                  const shiftWidthPct = Math.max(1, shiftRightPct - shiftLeftPct);

                  return (
                    <div
                      key={shift.id}
                      style={{
                        position: 'absolute',
                        left: `${shiftLeftPct}%`,
                        width: `${shiftWidthPct}%`,
                        top: 0,
                        bottom: 0,
                        background: '#1e293b',
                        borderLeft: '1px solid rgba(56, 189, 248, 0.4)',
                        borderRight: '1px solid rgba(56, 189, 248, 0.4)',
                        pointerEvents: 'none',
                        zIndex: 0
                      }}
                    />
                  );
                })}

                {/* Background Vertical Hour Grid Lines */}
                {hourTicks.map((tick, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: 'absolute',
                      left: `${tick.pct}%`,
                      top: 0,
                      bottom: 0,
                      width: '1px',
                      background: 'rgba(51, 65, 85, 0.45)',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  />
                ))}

                {/* SVG TRUCK LOAD WEIGHT GRAPH */}
                {hasJobs && (
                  <svg
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                      zIndex: 2
                    }}
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    {jobCurves.map((curve) => (
                      <g key={curve.jobId}>
                        <polygon points={curve.basePolygon} fill="rgba(34, 197, 94, 0.12)" />
                        {curve.overloadPolys.map((ov, oIdx) => (
                          <React.Fragment key={oIdx}>
                            <polygon points={ov.polygon} fill="rgba(239, 68, 68, 0.22)" />
                            <path d={ov.pathD} stroke="#ef4444" strokeWidth="1.5" vectorEffect="non-scaling-stroke" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </React.Fragment>
                        ))}
                        <path d={curve.basePathD} stroke="#22c55e" strokeWidth="1.5" vectorEffect="non-scaling-stroke" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                    ))}
                  </svg>
                )}

                {/* JOB BLOCKS */}
                {driverJobs.map(job => {
                  const { startSec, endSec } = getJobTimeRange(job);
                  const leftPct = timeToPct(startSec);
                  const endPct = timeToPct(endSec);
                  const widthPct = Math.max(2.5, endPct - leftPct);

                  const jobStops = stopsByJobId[job.id] || [];
                  const isOverlap = checkJobOverlap(driverJobs, job);
                  const truckObj = trucks.find(t => t.id === job.truck_id);
                  const matchingCurve = jobCurves.find(c => c.jobId === job.id);
                  const dynamicTop100 = matchingCurve?.top100Px || 32;

                  return (
                    <React.Fragment key={job.id}>
                      {/* 1. JOB TITLE */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${leftPct}%`,
                          top: '1px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: '0.67rem',
                          fontWeight: '700',
                          color: isOverlap ? '#f87171' : '#38bdf8',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          zIndex: 6,
                          cursor: 'pointer'
                        }}
                        onClick={() => onSelectJob && onSelectJob(job)}
                        title={`Mission: ${job.display_name || ('Job #' + job.id)}\nDépart: ${secondsToTimeString(startSec)}\nArrivée approx: ${secondsToTimeString(endSec)}${isOverlap ? '\n⚠️ CONFLIT HORAIRE : Chauffeur superposé sur plusieurs missions !' : ''}`}
                      >
                        {isOverlap && <AlertTriangle size={11} color="#ef4444" />}
                        <span>{truckObj ? truckObj.name : 'Camion'} • {job.display_name || `Mission #${job.id}`}</span>
                        <span style={{ color: '#94a3b8', fontWeight: '400' }}>({secondsToTimeString(startSec)} - {secondsToTimeString(endSec)})</span>
                      </div>

                      {/* 2. STOPS BUBBLES */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${leftPct}%`,
                          width: `${widthPct}%`,
                          top: '15px',
                          height: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          zIndex: 7
                        }}
                      >
                        {jobStops.map((stop) => {
                          const stopStartSec = timeToSeconds(stop.estimated_arrival || '08:00');
                          const stopServiceSec = (stop.service_time_mins || 10) * 60;
                          const jobSpan = Math.max(60, endSec - startSec);
                          const stopLeftRelPct = Math.max(0, Math.min(100, ((stopStartSec - startSec) / jobSpan) * 100));
                          const stopWidthRelPct = Math.max(1.5, Math.min(100 - stopLeftRelPct, ((stopServiceSec / jobSpan) * 100)));

                          const isDelivered = stop.status === 'delivered';
                          const isPickup = stop.type === 'pickup';
                          const stopBg = isDelivered 
                            ? 'rgba(16, 185, 129, 0.28)' 
                            : (isPickup ? 'rgba(245, 158, 11, 0.28)' : 'rgba(56, 189, 248, 0.25)');
                          const stopBorder = isDelivered 
                            ? '1.2px solid #10b981' 
                            : (isPickup ? '1.2px solid #f59e0b' : '1.2px solid #38bdf8');
                          const clientObj = clients.find(c => c.id === stop.client_id);

                          return (
                            <div
                              key={stop.id}
                              style={{
                                position: 'absolute',
                                left: `${stopLeftRelPct}%`,
                                width: `${Math.max(stopWidthRelPct, 2.5)}%`,
                                height: '12px',
                                background: stopBg,
                                border: stopBorder,
                                borderRadius: '3px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                zIndex: 8,
                                cursor: 'pointer',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.35)'
                              }}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipPos({ x: rect.left, y: rect.top - 10 });
                                setHoveredStop({ stop, client: clientObj, job });
                              }}
                              onMouseLeave={() => setHoveredStop(null)}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onSelectStop) onSelectStop(stop);
                              }}
                            />
                          );
                        })}
                      </div>

                      {/* 3. 100% TRUCK CAPACITY BOX */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${leftPct}%`,
                          width: `${widthPct}%`,
                          top: `${dynamicTop100}px`,
                          bottom: '0px',
                          background: 'rgba(56, 189, 248, 0.02)',
                          borderLeft: isOverlap ? '1.5px solid #ef4444' : '1.5px solid #38bdf8',
                          borderRight: isOverlap ? '1.5px solid #ef4444' : '1.5px solid #38bdf8',
                          borderTop: isOverlap ? '1.5px solid #ef4444' : '1.5px solid #38bdf8',
                          borderRadius: '3px 3px 0 0',
                          boxSizing: 'border-box',
                          zIndex: 3,
                          cursor: 'pointer'
                        }}
                        onClick={() => onSelectJob && onSelectJob(job)}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* ========================================================= */}
        {/* 2. VÉHICULES GROUPING VIEW */}
        {/* ========================================================= */}
        {timelineGrouping === 'truck' && activeTruckList.map(truck => {
          const truckJobs = jobsByTruck[truck.id] || [];
          const hasJobs = truckJobs.length > 0;
          const jobCurves = buildJobCurves(truckJobs);

          return (
            <div
              key={truck.id}
              style={{
                display: 'flex',
                alignItems: 'stretch',
                minHeight: '62px',
                height: '62px',
                borderBottom: '1px solid #334155',
                background: '#090d16',
                position: 'relative'
              }}
            >
              {/* TRUCK INFO LEFT COLUMN */}
              <div style={{
                width: '190px',
                minWidth: '190px',
                padding: '6px 12px',
                borderRight: '1px solid #334155',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#0f172a',
                zIndex: 10
              }}>
                <div style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  background: truck.isUnassigned ? '#ef4444' : (truck.color || '#38bdf8'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.74rem',
                  fontWeight: '800',
                  color: '#0f172a',
                  flexShrink: 0
                }}>
                  <Truck size={14} color="#0f172a" />
                </div>

                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#f1f5f9', lineHeight: '1.2', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {truck.name}
                  </div>
                  <div style={{ fontSize: '0.66rem', color: '#94a3b8', display: 'flex', gap: '4px' }}>
                    <span>{truck.license_plate || 'N/A'}</span>
                    {truck.max_payload_weight_kg && (
                      <span>• {truck.max_payload_weight_kg} kg</span>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT TIMELINE AXIS & TRACK AREA */}
              <div style={{
                flex: 1,
                position: 'relative',
                marginRight: '20px',
                display: 'flex',
                alignItems: 'stretch',
                background: '#090d16'
              }}>
                {/* Background Vertical Hour Grid Lines */}
                {hourTicks.map((tick, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: 'absolute',
                      left: `${tick.pct}%`,
                      top: 0,
                      bottom: 0,
                      width: '1px',
                      background: 'rgba(51, 65, 85, 0.45)',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  />
                ))}

                {/* SVG TRUCK LOAD WEIGHT GRAPH */}
                {hasJobs && (
                  <svg
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                      zIndex: 2
                    }}
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    {jobCurves.map((curve) => (
                      <g key={curve.jobId}>
                        <polygon points={curve.basePolygon} fill="rgba(34, 197, 94, 0.12)" />
                        {curve.overloadPolys.map((ov, oIdx) => (
                          <React.Fragment key={oIdx}>
                            <polygon points={ov.polygon} fill="rgba(239, 68, 68, 0.22)" />
                            <path d={ov.pathD} stroke="#ef4444" strokeWidth="1.5" vectorEffect="non-scaling-stroke" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </React.Fragment>
                        ))}
                        <path d={curve.basePathD} stroke="#22c55e" strokeWidth="1.5" vectorEffect="non-scaling-stroke" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                    ))}
                  </svg>
                )}

                {/* JOB BLOCKS PER TRUCK */}
                {truckJobs.map(job => {
                  const { startSec, endSec } = getJobTimeRange(job);
                  const leftPct = timeToPct(startSec);
                  const endPct = timeToPct(endSec);
                  const widthPct = Math.max(2.5, endPct - leftPct);

                  const jobStops = stopsByJobId[job.id] || [];
                  const isOverlap = checkJobOverlap(truckJobs, job);
                  const driverObj = drivers.find(d => d.id === job.driver_id);
                  const matchingCurve = jobCurves.find(c => c.jobId === job.id);
                  const dynamicTop100 = matchingCurve?.top100Px || 32;

                  return (
                    <React.Fragment key={job.id}>
                      {/* 1. JOB TITLE / DRIVER NAME ON TRUCK */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${leftPct}%`,
                          top: '1px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: '0.67rem',
                          fontWeight: '700',
                          color: isOverlap ? '#f87171' : (truck.color || '#38bdf8'),
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          zIndex: 6,
                          cursor: 'pointer'
                        }}
                        onClick={() => onSelectJob && onSelectJob(job)}
                        title={`Mission: ${job.display_name || ('Job #' + job.id)}\nChauffeur: ${driverObj ? `${driverObj.first_name} ${driverObj.last_name}` : 'Non assigné'}\nDépart: ${secondsToTimeString(startSec)}\nArrivée approx: ${secondsToTimeString(endSec)}${isOverlap ? '\n⚠️ CONFLIT VÉHICULE : Ce camion a plusieurs tournées prévues sur le même créneau horaire !' : ''}`}
                      >
                        {isOverlap && <AlertTriangle size={11} color="#ef4444" />}
                        <span>{driverObj ? `👤 ${driverObj.first_name} ${driverObj.last_name}` : '👤 Non assigné'} • {job.display_name || `Mission #${job.id}`}</span>
                        <span style={{ color: '#94a3b8', fontWeight: '400' }}>({secondsToTimeString(startSec)} - {secondsToTimeString(endSec)})</span>
                      </div>

                      {/* 2. STOPS BUBBLES */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${leftPct}%`,
                          width: `${widthPct}%`,
                          top: '15px',
                          height: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          zIndex: 7
                        }}
                      >
                        {jobStops.map((stop) => {
                          const stopStartSec = timeToSeconds(stop.estimated_arrival || '08:00');
                          const stopServiceSec = (stop.service_time_mins || 10) * 60;
                          const jobSpan = Math.max(60, endSec - startSec);
                          const stopLeftRelPct = Math.max(0, Math.min(100, ((stopStartSec - startSec) / jobSpan) * 100));
                          const stopWidthRelPct = Math.max(1.5, Math.min(100 - stopLeftRelPct, ((stopServiceSec / jobSpan) * 100)));

                          const isDelivered = stop.status === 'delivered';
                          const isPickup = stop.type === 'pickup';
                          const stopBg = isDelivered 
                            ? 'rgba(16, 185, 129, 0.28)' 
                            : (isPickup ? 'rgba(245, 158, 11, 0.28)' : 'rgba(56, 189, 248, 0.25)');
                          const stopBorder = isDelivered 
                            ? '1.2px solid #10b981' 
                            : (isPickup ? '1.2px solid #f59e0b' : '1.2px solid #38bdf8');
                          const clientObj = clients.find(c => c.id === stop.client_id);

                          return (
                            <div
                              key={stop.id}
                              style={{
                                position: 'absolute',
                                left: `${stopLeftRelPct}%`,
                                width: `${Math.max(stopWidthRelPct, 2.5)}%`,
                                height: '12px',
                                background: stopBg,
                                border: stopBorder,
                                borderRadius: '3px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                zIndex: 8,
                                cursor: 'pointer',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.35)'
                              }}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipPos({ x: rect.left, y: rect.top - 10 });
                                setHoveredStop({ stop, client: clientObj, job });
                              }}
                              onMouseLeave={() => setHoveredStop(null)}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onSelectStop) onSelectStop(stop);
                              }}
                            />
                          );
                        })}
                      </div>

                      {/* 3. 100% TRUCK CAPACITY BOX */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${leftPct}%`,
                          width: `${widthPct}%`,
                          top: `${dynamicTop100}px`,
                          bottom: '0px',
                          background: 'rgba(56, 189, 248, 0.02)',
                          borderLeft: isOverlap ? '1.5px solid #ef4444' : `1.5px solid ${truck.color || '#38bdf8'}`,
                          borderRight: isOverlap ? '1.5px solid #ef4444' : `1.5px solid ${truck.color || '#38bdf8'}`,
                          borderTop: isOverlap ? '1.5px solid #ef4444' : `1.5px solid ${truck.color || '#38bdf8'}`,
                          borderRadius: '3px 3px 0 0',
                          boxSizing: 'border-box',
                          zIndex: 3,
                          cursor: 'pointer'
                        }}
                        onClick={() => onSelectJob && onSelectJob(job)}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* STOP HOVER TOOLTIP */}
      {hoveredStop && (
        <div style={{
          position: 'fixed',
          left: `${tooltipPos.x}px`,
          top: `${tooltipPos.y}px`,
          transform: 'translate(-50%, -100%)',
          background: '#0f172a',
          border: hoveredStop.stop.type === 'pickup' ? '1px solid #f59e0b' : '1px solid #38bdf8',
          borderRadius: '6px',
          padding: '8px 12px',
          color: '#f8fafc',
          fontSize: '0.75rem',
          zIndex: 9999999,
          boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
          pointerEvents: 'none',
          minWidth: '230px'
        }}>
          <div style={{ fontWeight: '700', color: hoveredStop.stop.type === 'pickup' ? '#f59e0b' : '#38bdf8', marginBottom: '2px' }}>
            Stop #{hoveredStop.stop.sequence_order || hoveredStop.stop.id} • {hoveredStop.client?.name || 'Client'}
            <span style={{ fontSize: '0.68rem', fontWeight: '600', marginLeft: '6px', opacity: 0.9 }}>
              ({hoveredStop.stop.type === 'pickup' ? 'Ramasse' : 'Livraison'})
            </span>
          </div>
          <div style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>
            📍 {hoveredStop.stop.address || hoveredStop.client?.address || 'Adresse n/a'}
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginTop: '2px' }}>
            ⏱️ Arrivée: {hoveredStop.stop.estimated_arrival || '08:00'} ({hoveredStop.stop.service_time_mins || 10} min service)
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
            📦 {hoveredStop.stop.type === 'pickup' ? 'Charge Ramasse: +' : 'Charge Livraison: -'}{hoveredStop.stop.weight_kg || 0} kg • Volume: {hoveredStop.stop.volume_m3 || 0} m³
          </div>
        </div>
      )}
    </div>
  );
}
